/**
 * countdownApi — what the Countdown APP would paint on the page being edited (feature 0041 V6).
 *
 * The founder's question, and the reason this file exists: « pourquoi je dois la rajouter dans le builder,
 * le builder doit la lire directement de l'appli ». A campaign is placed by its own targeting rules in the
 * dashboard and injected by the page reads — it is not part of any page document, so the canvas has nothing
 * to render it from unless it ASKS.
 *
 * It asks the backend, never the raw config: `GET /api/builder/countdown` returns the campaigns ALREADY
 * RESOLVED by the very code the storefront uses (eligibility, targeting, the visitor line, the publication
 * shape). Re-deciding any of that in TypeScript would be the two-source drift this repo refuses — and the
 * drift would show up as an editor promising a countdown the shop does not serve.
 *
 * Reuses the authed `apiRequest` (Bearer from the JWT; the tenant is derived server-side from `store_id`,
 * so nothing about the store travels in this call).
 */
import { apiRequest } from './http';

/** The four surfaces, in the wire shape `ui-react`'s countdown components already parse defensively. */
export interface CountdownPreviewPayload {
  /** The buy-box banner for the bound preview product (product templates only). */
  readonly banner: unknown;
  /** The site-wide bar. */
  readonly bar: unknown;
  /** The corner popup. */
  readonly popup: unknown;
  /** productId → its image chip. */
  readonly badges: Readonly<Record<string, unknown>>;
}

export const EMPTY_COUNTDOWN_PREVIEW: CountdownPreviewPayload = {
  banner: null,
  bar: null,
  popup: null,
  badges: {},
};

/** The page types the app's campaigns target — the editor's template maps onto these four. */
export type CountdownPageType = 'home' | 'product' | 'collection' | 'catalog';

/**
 * The campaigns covering one page of this store.
 *
 * BEST-EFFORT by design: a 401/5xx, an offline laptop or a store whose Countdown app is not installed all
 * come back as "nothing to paint". The canvas is an editor — it must open and stay usable whatever this
 * answers, exactly as it does when the menus or the collections fail to resolve.
 */
export async function fetchCountdownPreview(
  pageType: CountdownPageType,
  productId?: string | null,
  cardProductIds: readonly string[] = [],
): Promise<CountdownPreviewPayload> {
  const params = new URLSearchParams({ pageType });
  if (productId) params.set('productId', productId);
  for (const id of cardProductIds) params.append('cardProductIds', id);
  try {
    const res = await apiRequest<CountdownPreviewPayload>(
      `/api/builder/countdown?${params.toString()}`,
    );
    return {
      banner: res?.banner ?? null,
      bar: res?.bar ?? null,
      popup: res?.popup ?? null,
      badges: res?.badges ?? {},
    };
  } catch {
    return EMPTY_COUNTDOWN_PREVIEW;
  }
}

/**
 * Save one campaign's LOOK (feature 0041 V7) — the builder's half of « the builder is just for the
 * design ». The timer, the targeting and the texts are not sent: this writes `design` and nothing else,
 * so a colour change can never disturb what the app decided.
 *
 * Returns false on any refusal (a 404 means the campaign was deleted in the dashboard while the editor
 * was open). The panel says so rather than leaving the canvas showing something the shop will not serve.
 */
export async function saveCountdownDesign(
  campaignId: string,
  design: Record<string, unknown>,
): Promise<boolean> {
  try {
    await apiRequest(`/api/builder/countdown/${encodeURIComponent(campaignId)}/design`, {
      method: 'PUT',
      // `apiRequest` serialises the body itself. Passing a string here double-encoded it: the wire
      // carried a JSON STRING, and Spring would have refused to bind it to a Map (caught by the walk,
      // 29/09/2026 — the canvas repainted and the shop was never told).
      body: design,
    });
    return true;
  } catch {
    return false;
  }
}
